/**
 * Centralised env-var accessors.
 *
 * Replaces the per-file `DEFAULT_BACKEND_URL` / `DEFAULT_ROTAGEN_URL` /
 * `DEFAULT_TENANT_ID` constants that used to live duplicated across
 * every `src/api/*-api.ts` client (and in `src/dev-shim.ts`).
 *
 * Backend + rotagen URLs **throw** when their env var is unset rather
 * than falling back to a hardcoded `localhost:5203` / `5201`. In every
 * legitimate code path the variables are set:
 *
 * - `yarn dev:isolate` injects them at process-spawn time
 *   (`scripts/dev-isolate.ts`)
 * - `apps/template-builder-evo/.env.development` declares them for
 *   bare `yarn dev`
 * - SST stacks set them per stage
 * - Vitest specs mock `fetch`/the API clients so these never fire
 *
 * So a thrown error here means a real misconfiguration — surfacing it
 * loudly is better than silently fetching against the wrong host.
 *
 * These backend/rotagen URL accessors are only ever called from the
 * standalone app's own config-building glue (`main.tsx`'s `buildDevConfig`,
 * `dev-shim.ts`) — the published library gets `backendApiUrl`/`rotagenApiUrl`
 * from the host-supplied `TemplateBuilderConfig` instead (`config-registry.ts`),
 * never from `import.meta.env`.
 */

/**
 * Returns the Rotaready backend (BFF) base URL from `VITE_BACKEND_API_URL`.
 * Throws if unset — see module JSDoc.
 */
export function getBackendUrl(): string {
  const url = import.meta.env.VITE_BACKEND_API_URL as string | undefined;
  if (!url) {
    throw new Error(
      'VITE_BACKEND_API_URL is not set. Run via `yarn dev:isolate`, or set it in `.env.development.local` for a bare `yarn dev`.',
    );
  }
  return url;
}

/**
 * Returns the rotagen base URL from `VITE_ROTAGEN_API_URL`. Throws if
 * unset — see module JSDoc.
 */
export function getRotagenUrl(): string {
  const url = import.meta.env.VITE_ROTAGEN_API_URL as string | undefined;
  if (!url) {
    throw new Error(
      'VITE_ROTAGEN_API_URL is not set. Run via `yarn dev:isolate`, or set it in `.env.development.local` for a bare `yarn dev`.',
    );
  }
  return url;
}

/**
 * Returns the OpenComponents registry base URL from
 * `VITE_OC_REGISTRY_URL` — the host that serves the OC client script
 * (`/registry/oc-client/client.js`) and the individual component
 * bundles (`/registry/<component-name>/1/?...`). Throws if unset.
 */
export function getOcRegistryUrl(): string {
  const url = import.meta.env.VITE_OC_REGISTRY_URL as string | undefined;
  if (!url) {
    throw new Error(
      'VITE_OC_REGISTRY_URL is not set. Declare it in `.env.development` (staging) or your stage env file (production).',
    );
  }
  return url;
}

/**
 * Returns the Evo sentiment-API host from
 * `VITE_FEATURE_FEEDBACK_API_HOST`. Throws if unset — wrong-env
 * feedback ends up in the wrong sentiment dashboard, so a default
 * would be more dangerous than a loud failure.
 */
export function getFeatureFeedbackApiHost(): string {
  const url = import.meta.env.VITE_FEATURE_FEEDBACK_API_HOST as string | undefined;
  if (!url) {
    throw new Error(
      'VITE_FEATURE_FEEDBACK_API_HOST is not set. Declare it in `.env.development` (staging) or your stage env file (production).',
    );
  }
  return url;
}

/**
 * Returns the Evo feature-feedback account token from
 * `VITE_FEATURE_FEEDBACK_ACCOUNT_TOKEN`. Throws if unset.
 */
export function getFeatureFeedbackAccountToken(): string {
  const token = import.meta.env.VITE_FEATURE_FEEDBACK_ACCOUNT_TOKEN as string | undefined;
  if (!token) {
    throw new Error(
      'VITE_FEATURE_FEEDBACK_ACCOUNT_TOKEN is not set. Declare it in `.env.development` or your stage env file.',
    );
  }
  return token;
}
